/**
 * RetailerPriceBookGate — a signed-in retailer user on the MAIN site works with
 * their retailer's price book for the whole session, so the customer beside
 * them sees the store's real prices. On a branded portal the portal's book
 * stays (they are its customer there).
 *
 * Swaps the loaded product table when the active retailer changes (sign-in,
 * sign-out, the /retaileradmin retailer pick) and remounts the page tree so
 * every calculation reads the new table. Renders a one-line note while a
 * retailer book is in use.
 */

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { usePartnerBranding } from '../context/PartnerBrandingContext';
import { loadProducts, getLoadedRetailerId } from '../data/productTable';
import { setTrackingContext } from '../utils/gtmTracking';

export const RetailerPriceBookGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation('results');
  const { user, isChecking } = useAuth();
  const { partner } = usePartnerBranding();
  const wanted = partner ? (partner.id ?? 1) : (user?.activeRetailerId ?? 1);
  const [loadedFor, setLoadedFor] = useState<number | null>(() => getLoadedRetailerId() ?? 1);
  const [swapping, setSwapping] = useState(false);

  useEffect(() => {
    if (isChecking) return;
    if (getLoadedRetailerId() === wanted) { setLoadedFor(wanted); return; }
    let cancelled = false;
    setSwapping(true);
    loadProducts(wanted, true)
      .catch(() => false)
      .then(() => {
        if (cancelled) return;
        const id = getLoadedRetailerId();
        setTrackingContext({ retailerId: id, priceBook: id === null ? 'fallback' : id === 1 ? 'msrp' : 'retailer' });
        setLoadedFor(wanted);
      })
      .finally(() => { if (!cancelled) setSwapping(false); });
    return () => { cancelled = true; };
  }, [wanted, isChecking]);

  if (swapping) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '40vh' }}>
        <div className="loading-spinner" style={{ width: 32, height: 32, border: '4px solid #e5e7eb', borderTop: '4px solid #1a1a2e', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  const retailerName = !partner && user?.activeRetailerId
    ? (user.retailerMemberships ?? []).find(m => m.retailerId === user.activeRetailerId)?.retailerName ?? null
    : null;

  return (
    <React.Fragment key={loadedFor ?? 0}>
      {retailerName && (
        <div className="text-center small text-muted py-1" style={{ background: '#f8f9fa' }}>
          {t('shared.retailerBook', { retailer: retailerName })}
        </div>
      )}
      {children}
    </React.Fragment>
  );
};

export default RetailerPriceBookGate;
